import { defineStore } from 'pinia';
import api from '@/api';
import { isResultCancelled } from '@/utils/resultParser';
import { isActiveLiteIntegration, LITE_RESULTS_PAGE_LIMIT } from '@/utils/integrationMode';

function normalizeCancelledFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === 't' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

function mapResultRows(results: unknown[]): Result[] {
  return results.map((row) => {
    const result = row as Result;
    return {
      ...result,
      cancelled: normalizeCancelledFlag(result.cancelled) || isResultCancelled(result),
    };
  });
}

function buildResultsPayload(filters: ResultsFilters, page: number, limit: number) {
  const payload: Record<string, string | number> = { page, limit };

  if (filters.drawType) {
    payload.drawType = filters.drawType;
  }
  if (filters.drawDate) {
    payload.drawDate = filters.drawDate;
  }
  if (filters.gameSlug) {
    payload.gameSlug = filters.gameSlug;
  }
  if (filters.drawId) {
    payload.drawId = filters.drawId;
  }

  return payload;
}

export interface Result {
  id: number;
  event_id: number;
  admin_id: number | null;
  provider_id: number;
  result: string;
  draw_date: string;
  created_at: string;
  updated_at: string;
  provider: string;
  game_name: string;
  game_slug: string;
  game_logo: string;
  cancelled: boolean;
}

export interface ResultsFilters {
  drawDate?: string;
  drawType?: string;
  drawId?: string;
  gameSlug?: string;
}

export const useResultsStore = defineStore('results', {
  state: () => ({
    results: [] as Result[],
    games: [] as any[],
    loading: false,
    loadingMore: false,
    error: null as string | null,
    filters: {} as ResultsFilters,
    pagination: {
      total_count: 0,
      total_pages: 1,
      current_page: 1,
      limit: 20,
    },
  }),
  getters: {
    hasMorePages(state): boolean {
      return state.pagination.current_page < state.pagination.total_pages;
    },
  },
  actions: {
    setFilters(filters: ResultsFilters) {
      this.filters = { ...filters };
    },
    async fetchResults(page = 1) {
      this.loading = true;
      this.error = null;
      // Clear stale cards immediately so filters show loading feedback
      if (page === 1) {
        this.results = [];
      }
      try {
        const requestLimit = isActiveLiteIntegration() ? LITE_RESULTS_PAGE_LIMIT : this.pagination.limit;
        const payload = buildResultsPayload(this.filters, page, requestLimit);
        const { data } = await api.post('/results', payload);
        const { results, games, paginator } = this.parseResultsResponse(data);

        this.results = mapResultRows(results);
        this.games = games;
        this.pagination = paginator;
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch results';
      } finally {
        this.loading = false;
      }
    },
    async loadMoreResults() {
      if (!this.hasMorePages || this.loading || this.loadingMore) {
        return;
      }

      this.loadingMore = true;
      this.error = null;

      try {
        const nextPage = this.pagination.current_page + 1;
        const requestLimit = isActiveLiteIntegration() ? LITE_RESULTS_PAGE_LIMIT : this.pagination.limit;
        const payload = buildResultsPayload(this.filters, nextPage, requestLimit);
        const { data } = await api.post('/results', payload);
        const { results, paginator } = this.parseResultsResponse(data);

        this.results = [...this.results, ...mapResultRows(results)];
        this.pagination = paginator;
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to load more results';
      } finally {
        this.loadingMore = false;
      }
    },
    parseResultsResponse(data: any) {
      let results: unknown[] = [];
      let games: any[] = [];
      let paginator = { ...this.pagination };

      if (data?.result) {
        if (Array.isArray(data.result.results)) {
          results = data.result.results;
        } else if (data.result.result && Array.isArray(data.result.result.results)) {
          results = data.result.result.results;
        }

        if (Array.isArray(data.result.games)) {
          games = data.result.games;
        } else if (data.result.result && Array.isArray(data.result.result.games)) {
          games = data.result.result.games;
        }
      }

      if (data?.paginator) {
        paginator = { ...paginator, ...data.paginator };
      }

      return { results, games, paginator };
    },
    setPage(page: number) {
      this.pagination.current_page = page;
      this.fetchResults(page);
    },
  },
});
